import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, LogOut, Moon, Settings, Sun } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useThemeStore } from '../stores/theme';

interface UserMenuProps {
  email: string;
  isAdmin: boolean;
  onLogout: () => void;
}

export function UserMenu({ email, isAdmin, onLogout }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { theme, setTheme } = useThemeStore();
  const isDark = theme === 'dark';

  const displayName = (email.split('@')[0] ?? email)
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const initial = displayName.charAt(0).toUpperCase();

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2
                   hover:bg-surface-2 transition-all duration-150"
      >
        <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full
                         bg-gradient-to-br from-brand-500 to-brand-700 text-[12px] font-bold text-white
                         ring-2 ring-surface-1 shadow-sm">
          {initial}
          <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-400
                           border-2 border-surface-1" />
        </span>
        <span className="flex flex-col items-start leading-none min-w-0 flex-1">
          <span className="text-[12.5px] font-semibold text-fg truncate max-w-full">{displayName}</span>
          <span className="text-[11px] text-fg-3 mt-0.5 truncate max-w-full">{email}</span>
        </span>
        <ChevronDown
          size={11}
          className={`shrink-0 text-fg-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Popover */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="absolute bottom-full left-0 z-50 mb-2 w-72 overflow-hidden
                       rounded-2xl border border-surface-3 bg-surface-1
                       shadow-2xl shadow-black/25"
          >
            {/* Account header */}
            <div className="relative flex items-center gap-3.5 px-4 py-4 overflow-hidden border-b border-surface-3">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-500/10 to-brand-600/5 pointer-events-none" />
              <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full
                               bg-gradient-to-br from-brand-500 to-brand-700
                               text-[15px] font-bold text-white ring-2 ring-surface-1 shadow-lg">
                {initial}
                <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-emerald-400
                                 border-2 border-surface-1 shadow-sm" />
              </span>
              <div className="min-w-0 flex-1 relative">
                <p className="truncate text-[13.5px] font-semibold text-fg leading-tight">{displayName}</p>
                <p className="truncate text-[11.5px] text-fg-3 mt-0.5 leading-tight">{email}</p>
                <span className="inline-flex items-center gap-1 mt-1.5 rounded-full
                                 bg-brand-500/12 px-2 py-0.5 text-[10px] font-medium text-brand-500
                                 ring-1 ring-brand-500/25">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-500 animate-pulse" />
                  Active
                </span>
              </div>
            </div>

            {/* Settings */}
            <div className="p-1.5">
              <button
                type="button"
                onClick={() => { setOpen(false); navigate('/settings/account'); }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5
                           hover:bg-surface-3 transition-colors text-left group"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-surface-3
                                 group-hover:bg-surface-4 transition-colors shrink-0">
                  <Settings size={13} className="text-fg-3" />
                </span>
                <div>
                  <p className="text-[12.5px] font-medium text-fg leading-none">Settings</p>
                  <p className="text-[11px] text-fg-3 mt-0.5">
                    {isAdmin ? 'Account, team & branding' : 'View account settings'}
                  </p>
                </div>
              </button>
            </div>
            <div className="mx-3 h-px bg-surface-3" />

            {/* Appearance */}
            <div className="p-1.5">
              <button
                type="button"
                onClick={() => setTheme(isDark ? 'light' : 'dark')}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5
                           hover:bg-surface-3 transition-colors text-left group"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-surface-3
                                 group-hover:bg-surface-4 transition-colors shrink-0">
                  {isDark ? <Sun size={13} className="text-fg-3" /> : <Moon size={13} className="text-fg-3" />}
                </span>
                <div className="flex-1">
                  <p className="text-[12.5px] font-medium text-fg leading-none">Appearance</p>
                  <p className="text-[11px] text-fg-3 mt-0.5">{isDark ? 'Switch to light' : 'Switch to dark'}</p>
                </div>
              </button>
            </div>

            <div className="mx-3 h-px bg-surface-3" />

            {/* Sign out */}
            <div className="p-1.5">
              <button
                type="button"
                onClick={() => { setOpen(false); onLogout(); }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5
                           hover:bg-red-500/8 transition-colors text-left group"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg shrink-0
                                 bg-red-500/10 group-hover:bg-red-500/15 transition-colors">
                  <LogOut size={13} className="text-red-400" />
                </span>
                <div>
                  <p className="text-[12.5px] font-medium text-red-400 leading-none">Sign out</p>
                  <p className="text-[11px] text-fg-3 mt-0.5">End your session</p>
                </div>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
